import {
  DEFAULT_YAK_TRAILS_CONFIG,
  YAK_TRAILS_SOCKET_CLOSE_CODES,
  decodeServerMessage,
  encodeClientMessage,
  terminalRoomSocketClose,
  type RoomConnection,
  type RoomDisconnectReason,
  type SteeringInput,
} from "@games-monorepo/yak-trails-core";

import { YAK_TRAILS_CLIENT_TUNING } from "../tuning";
import { BaseRoomTransport } from "./BaseRoomTransport";

function asArrayBuffer(frame: Uint8Array): ArrayBuffer {
  const buffer = new ArrayBuffer(frame.byteLength);
  new Uint8Array(buffer).set(frame);
  return buffer;
}

export class WebSocketRoomTransport extends BaseRoomTransport {
  private socket: WebSocket | null = null;
  private connection: RoomConnection | null = null;
  private sequence = 0;
  private closedIntentionally = false;
  private reconnectAttempt = 0;
  private reconnectExpiresAtMs = 0;
  private reconnectTimer: ReturnType<typeof globalThis.setTimeout> | undefined;
  private heartbeatTimer: ReturnType<typeof globalThis.setInterval> | undefined;
  private foregroundProbeTimer: ReturnType<typeof globalThis.setTimeout> | undefined;
  private hasWelcomed = false;
  private smoothedLatencyMs: number | undefined;
  private lastPongAtMs = 0;
  private latestSteeringInput: SteeringInput | undefined;

  async connect(connection: RoomConnection): Promise<void> {
    this.connection = connection;
    this.closedIntentionally = false;
    this.reconnectExpiresAtMs = 0;
    await this.openSocket();
  }

  sendSteering(input: SteeringInput): void {
    this.latestSteeringInput = input;
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(asArrayBuffer(encodeClientMessage({ v: 1, type: "input", input })));
  }

  requestRespawn(): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(asArrayBuffer(encodeClientMessage({ v: 1, type: "respawn", seq: ++this.sequence })));
  }

  requestResync(): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(asArrayBuffer(encodeClientMessage({ v: 1, type: "resync", seq: ++this.sequence })));
  }

  refreshAfterForeground(): void {
    if (this.closedIntentionally || !this.connection || this.foregroundProbeTimer !== undefined) return;
    const socket = this.socket;
    if (socket?.readyState === WebSocket.OPEN) {
      const probeStartedAt = Date.now();
      this.sendPing(socket);
      this.requestResync();
      this.foregroundProbeTimer = globalThis.setTimeout(() => {
        this.foregroundProbeTimer = undefined;
        if (this.socket !== socket || this.lastPongAtMs >= probeStartedAt) return;
        socket.close(YAK_TRAILS_SOCKET_CLOSE_CODES.clientHealthRestart, "foreground_probe_timeout");
      }, YAK_TRAILS_CLIENT_TUNING.transport.foregroundHealthTimeoutMs);
      return;
    }

    if (this.reconnectExpiresAtMs === 0) {
      this.reconnectExpiresAtMs = Date.now() + DEFAULT_YAK_TRAILS_CONFIG.network.reconnectGraceMs;
    }
    if (this.reconnectTimer !== undefined) globalThis.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    if (socket?.readyState === WebSocket.CONNECTING) {
      socket.close(YAK_TRAILS_SOCKET_CLOSE_CODES.clientHealthRestart, "foreground_reconnect");
    }
    else this.scheduleReconnect(true);
  }

  async disconnect(_reason: RoomDisconnectReason): Promise<void> {
    this.closedIntentionally = true;
    if (this.reconnectTimer !== undefined) globalThis.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    this.stopHeartbeat();
    this.stopForegroundProbe();
    this.socket?.close(1000, "client_exit");
    this.socket = null;
  }

  private openSocket(): Promise<void> {
    const connection = this.connection;
    if (!connection) return Promise.reject(new Error("Missing Yak Trails room connection"));
    return new Promise((resolve, reject) => {
      const socket = new WebSocket(connection.socketUrl, ["yak-trails-v1", `ticket.${connection.ticket}`]);
      socket.binaryType = "arraybuffer";
      this.socket = socket;
      socket.onopen = () => {
        this.emit({ type: "network", quality: "good" });
        this.startHeartbeat(socket);
        resolve();
      };
      socket.onmessage = (event) => {
        try {
          const message = decodeServerMessage(event.data as ArrayBuffer);
          if (message.type === "welcome") {
            const reconnected = this.hasWelcomed;
            this.hasWelcomed = true;
            if (message.reconnectToken && this.connection) this.connection.ticket = message.reconnectToken;
            this.reconnectAttempt = 0;
            this.reconnectExpiresAtMs = 0;
            this.stopForegroundProbe();
            this.emit({ type: "connected", playerId: message.playerId, ...(reconnected ? { reconnected: true } : {}) });
            if (this.latestSteeringInput && socket.readyState === WebSocket.OPEN) {
              socket.send(asArrayBuffer(encodeClientMessage({
                v: 1,
                type: "input",
                input: this.latestSteeringInput,
              })));
            }
          }
          else if (message.type === "snapshot") this.emit({ type: "snapshot", snapshot: message.snapshot, ...(message.territory ? { territory: message.territory } : {}) });
          else if (message.type === "territory") this.emit({ type: "territory", patch: message.patch });
          else if (message.type === "events") this.emit({ type: "events", events: message.events });
          else if (message.type === "result") this.emit({ type: "result", result: message.result });
          else if (message.type === "pong") {
            this.lastPongAtMs = Date.now();
            this.stopForegroundProbe();
            const measuredLatencyMs = Math.max(0, Date.now() - message.clientTimeMs);
            this.smoothedLatencyMs = this.smoothedLatencyMs === undefined
              ? measuredLatencyMs
              : this.smoothedLatencyMs * YAK_TRAILS_CLIENT_TUNING.transport.latencySmoothingPreviousWeight
                + measuredLatencyMs * (1 - YAK_TRAILS_CLIENT_TUNING.transport.latencySmoothingPreviousWeight);
            this.emit({
              type: "network",
              quality: this.smoothedLatencyMs > YAK_TRAILS_CLIENT_TUNING.transport.degradedLatencyMs ? "degraded" : "good",
              latencyMs: Math.round(this.smoothedLatencyMs),
            });
          }
          else if (message.type === "error") {
            if (!message.retryable) this.closedIntentionally = true;
            this.emit({ type: "error", code: message.code, message: message.message, retryable: message.retryable });
          }
        } catch {
          // The typed close handler reports one terminal protocol error and
          // prevents an incompatible room from entering the reconnect loop.
          socket.close(1002, "protocol_error");
        }
      };
      socket.onerror = () => {
        if (socket.readyState !== WebSocket.OPEN) reject(new Error("Unable to connect to Yak Trails"));
      };
      socket.onclose = (event) => {
        if (this.socket !== socket) return;
        this.socket = null;
        this.stopHeartbeat();
        this.stopForegroundProbe();
        this.emit({ type: "network", quality: "offline" });
        const terminal = terminalRoomSocketClose(event.code, event.reason);
        if (!this.closedIntentionally && terminal) {
          this.closedIntentionally = true;
          this.emit({
            type: "error",
            code: terminal.code,
            message: terminal.message,
            retryable: false,
          });
        } else if (!this.closedIntentionally) {
          if (this.reconnectAttempt === 0) this.reconnectExpiresAtMs = Date.now() + DEFAULT_YAK_TRAILS_CONFIG.network.reconnectGraceMs;
          this.scheduleReconnect();
        }
      };
    });
  }

  private startHeartbeat(socket: WebSocket): void {
    this.stopHeartbeat();
    const sendPing = () => {
      this.sendPing(socket);
    };
    sendPing();
    this.heartbeatTimer = globalThis.setInterval(sendPing, YAK_TRAILS_CLIENT_TUNING.transport.heartbeatIntervalMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== undefined) globalThis.clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = undefined;
  }

  private stopForegroundProbe(): void {
    if (this.foregroundProbeTimer !== undefined) globalThis.clearTimeout(this.foregroundProbeTimer);
    this.foregroundProbeTimer = undefined;
  }

  private sendPing(socket: WebSocket): void {
    if (this.socket !== socket || socket.readyState !== WebSocket.OPEN) return;
    socket.send(asArrayBuffer(encodeClientMessage({
      v: 1,
      type: "ping",
      nonce: ++this.sequence,
      clientTimeMs: Date.now(),
    })));
  }

  private scheduleReconnect(immediate = false): void {
    if (this.reconnectTimer !== undefined) return;
    if (Date.now() >= this.reconnectExpiresAtMs) {
      this.emit({ type: "error", code: "reconnect_expired", message: "The reconnect window expired.", retryable: true });
      return;
    }
    this.reconnectAttempt += 1;
    this.emit({ type: "reconnecting", attempt: this.reconnectAttempt, expiresAtMs: this.reconnectExpiresAtMs });
    const delay = immediate
      ? 0
      : Math.min(
        YAK_TRAILS_CLIENT_TUNING.transport.reconnectMaximumDelayMs,
        YAK_TRAILS_CLIENT_TUNING.transport.reconnectInitialDelayMs * 2 ** (this.reconnectAttempt - 1),
      );
    this.reconnectTimer = globalThis.setTimeout(() => {
      this.reconnectTimer = undefined;
      if (!this.closedIntentionally) void this.openSocket()
        .then(() => this.requestResync())
        .catch(() => this.scheduleReconnect());
    }, delay);
  }
}
